/**
 * Which schools override the district's OVS split — pure, for the district
 * view of the My School pre-fill card.
 *
 * OVS is set by the district, with school-level overrides (Sheila,
 * 2026-11-04). The district view has to say which schools are NOT following
 * it, or an admin edits the district and believes it reached everyone — the
 * exact confusion behind the New Hyde Park report.
 */

import type { DistrictSplitMatrix } from '@/api/location-settings'
import type { Mealtime } from '@/lib/mealtime'

export interface SchoolOverrideSummary {
  locationId: string
  locationName: string
  /** Mealtimes where the school sets at least one number of its own. */
  mealtimes: Mealtime[]
  /** True when any of those mealtimes also carries age-group values. */
  ageGroups: boolean
}

/**
 * Schools with any number of their own, in the matrix's campus order.
 *
 * A campus cell whose `source` is the campus tier is a school override; a
 * non-zero `columnOverrideCount` means age-group rows. Weekday rules are left
 * to the full comparison page — they are the rare case and are listed there.
 */
export function schoolsWithOverrides(matrix: DistrictSplitMatrix | undefined): SchoolOverrideSummary[] {
  if (!matrix) {
    return []
  }
  const byLocation = new Map<string, SchoolOverrideSummary>()
  for (const board of matrix.mealtimes) {
    for (const campus of board.campuses) {
      const ownCell = Object.values(campus.cells).some((cell) => cell.source === 'LOCATION')
      const ageGroups = campus.columnOverrideCount > 0
      if (!ownCell && !ageGroups) {
        continue
      }
      const entry = byLocation.get(campus.locationId) ?? {
        locationId: campus.locationId,
        locationName: campus.locationName,
        mealtimes: [],
        ageGroups: false,
      }
      entry.mealtimes.push(board.mealtime)
      entry.ageGroups ||= ageGroups
      byLocation.set(campus.locationId, entry)
    }
  }
  return [...byLocation.values()]
}
